import { useMemo } from "react";
import { ChevronLeft } from "lucide-react";
import { dictionary, Language } from "../../../utils/dictionary";
import { Product } from "../../products/ProductInterface";
import { AdTableRow } from "../../../services/meta/metaService";

interface ProductHeaderProps {
  product: Product;
  productAds: AdTableRow[];
  language: Language;
  onBack: () => void;
}

export const ProductHeader = ({
  product,
  productAds,
  language,
  onBack,
}: ProductHeaderProps) => {
  const aggregates = useMemo(() => computeAggregates(productAds), [productAds]);
  const thumbnail = product.images?.[0];

  return (
    <div className="bg-white border-b border-gray-200 px-8 py-4">
      <div className="text-xs text-gray-400 mb-3 flex items-center gap-1">
        <button type="button" onClick={onBack} className="hover:text-gray-900 transition-colors">
          {dictionary["performance-back-to-products"]?.[language] ?? "Productos"}
        </button>
        <span>›</span>
        <span className="text-gray-900 font-medium truncate">{product.name}</span>
      </div>

      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={onBack}
          className="flex-shrink-0 w-9 h-9 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50"
          aria-label="Back"
        >
          <ChevronLeft className="w-4 h-4 text-gray-500" />
        </button>

        <div className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-gray-100">
          {thumbnail ? (
            <img src={thumbnail} alt={product.name} className="w-full h-full object-cover" />
          ) : null}
        </div>

        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold text-gray-900 truncate">{product.name}</h1>
          {product.description && (
            <p className="text-xs text-gray-500 truncate">{product.description}</p>
          )}
        </div>

        <div className="flex items-stretch divide-x divide-gray-200 flex-shrink-0">
          <MetricCell label={dictionary["performance-ads-count"]?.[language] ?? "Ads"} value={String(aggregates.ads)} />
          <MetricCell
            label={dictionary["performance-metric-ctr"]?.[language] ?? "CTR"}
            value={aggregates.avgCtr.toFixed(2) + "%"}
          />
          <MetricCell
            label={dictionary["performance-metric-hook-rate"]?.[language] ?? "Hook rate"}
            value={(aggregates.avgHookRate * 100).toFixed(1) + "%"}
          />
          <MetricCell
            label={dictionary["performance-metric-spend"]?.[language] ?? "Gasto"}
            value={formatSpend(aggregates.totalSpend)}
          />
          <MetricCell
            label={dictionary["performance-metric-roas"]?.[language] ?? "ROAS"}
            value={aggregates.avgRoas.toFixed(2) + "x"}
          />
        </div>
      </div>
    </div>
  );
};

const MetricCell = ({ label, value }: { label: string; value: string }) => (
  <div className="px-4 first:pl-0 last:pr-0">
    <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{label}</p>
    <p className="text-base font-semibold text-gray-900 tabular-nums">{value}</p>
  </div>
);

/** Formats spend with thousand separators and at most 2 decimals (only shown when the value has them). */
const formatSpend = (n: number): string => {
  const hasDecimals = n % 1 !== 0;
  return "$" + n.toLocaleString("en-US", {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  });
};

const computeAggregates = (ads: AdTableRow[]) => {
  if (ads.length === 0) {
    return { ads: 0, avgCtr: 0, avgHookRate: 0, totalSpend: 0, avgRoas: 0 };
  }
  const total = ads.length;
  const avgCtr = ads.reduce((acc, a) => acc + (a.ctr ?? 0), 0) / total;
  const avgHookRate = ads.reduce((acc, a) => acc + (a.hook_rate ?? 0), 0) / total;
  const totalSpend = ads.reduce((acc, a) => acc + (a.spend ?? 0), 0);
  const roasSamples = ads.filter((a) => a.roas > 0);
  const avgRoas = roasSamples.length > 0
    ? roasSamples.reduce((acc, a) => acc + a.roas, 0) / roasSamples.length
    : 0;
  return { ads: total, avgCtr, avgHookRate, totalSpend, avgRoas };
};

export default ProductHeader;
